import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../../i18n/LanguageContext";
import { WHATSAPP_URL } from "../../i18n/translations";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLink = (href, label, testId) => (
    <a
      key={href}
      href={href}
      onClick={() => setOpen(false)}
      data-testid={testId}
      className="text-sm text-white/70 hover:text-white transition-colors underline-grow"
    >
      {label}
    </a>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0B0E]/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="header-logo-link">
          <Logo size={40} />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {navLink("#features", t.nav.features, "nav-features")}
          {navLink("#how", t.nav.how, "nav-how")}
          {navLink("#faq", t.nav.faq, "nav-faq")}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangSwitch lang={lang} setLang={setLang} />
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="header-cta-demo"
            className="bg-brand-orange hover:bg-brand-orangeHover text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all shadow-[0_0_20px_rgba(249,115,22,0.35)] hover:shadow-[0_0_30px_rgba(249,115,22,0.55)] hover:-translate-y-0.5"
          >
            {t.nav.demo}
          </a>
        </div>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="menu"
          data-testid="header-hamburger"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#0A0B0E]/95 backdrop-blur-xl">
          <div className="px-5 py-5 flex flex-col gap-4">
            {navLink("#features", t.nav.features, "mobile-nav-features")}
            {navLink("#how", t.nav.how, "mobile-nav-how")}
            {navLink("#faq", t.nav.faq, "mobile-nav-faq")}
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <LangSwitch lang={lang} setLang={setLang} />
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-cta-demo"
                className="bg-brand-orange text-white rounded-full px-5 py-2.5 text-sm font-medium"
              >
                {t.nav.demo}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LangSwitch({ lang, setLang }) {
  return (
    <div
      role="group"
      aria-label="language switch"
      className="flex items-center bg-white/5 border border-white/10 rounded-full p-1"
      data-testid="lang-switch"
    >
      <button
        onClick={() => setLang("es")}
        data-testid="lang-es"
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          lang === "es" ? "bg-brand-orange text-white" : "text-white/70 hover:text-white"
        }`}
      >
        ES
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          lang === "en" ? "bg-brand-orange text-white" : "text-white/70 hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );
}
